import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { DiffReport } from '@/components/diff-report';
import { formatDateTime } from '@/lib/format';

export const siteDiffLatestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId/diff',
  component: SiteDiffLatestPage,
});

export const siteDiffPairRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId/diff/$a/$b',
  component: SiteDiffPairPage,
});

function SiteDiffLatestPage() {
  const { siteId } = siteDiffLatestRoute.useParams();

  const diffQuery = useQuery({
    queryKey: ['sites', siteId, 'diff', 'latest'] as const,
    retry: false,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/diff', { params: { path: { siteId } } });
      if (error) throw error;
      return data;
    },
  });

  const crawlsQuery = useQuery({
    queryKey: queryKeys.sites.crawls(siteId, 1),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/crawls', {
        params: { path: { siteId }, query: { page: 1, pageSize: 10 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const items = crawlsQuery.data?.items ?? [];
  if (!diffQuery.isLoading && items.length < 2) {
    return (
      <div className="rounded-md border bg-background p-6 text-sm text-muted-foreground" data-testid="diff-requires-two">
        A diff requires at least two completed crawls. This site has {items.length}.
      </div>
    );
  }

  if (diffQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading diff…</div>;
  }
  if (diffQuery.isError || !diffQuery.data) {
    return (
      <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
        Diff unavailable.
      </div>
    );
  }

  const report = diffQuery.data;
  const earlier = items.find((c) => c.id === report.baselineCrawlId);
  const later = items.find((c) => c.id === report.currentCrawlId);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to="/sites/$siteId"
          params={{ siteId }}
          className="text-xs text-muted-foreground hover:underline"
          data-testid="site-diff-back-link"
        >
          ← Back to site
        </Link>
        <h1 className="text-xl font-semibold">Scan diff — latest vs previous</h1>
        <p className="text-xs text-muted-foreground">
          {earlier ? `Earlier ${formatDateTime(earlier.startedAt)}` : 'Earlier crawl unknown'} ·{' '}
          {later ? `Later ${formatDateTime(later.startedAt)}` : 'Later crawl unknown'}
        </p>
      </div>
      <DiffReport
        report={report}
        earlierLabel={earlier ? formatDateTime(earlier.startedAt) : 'earlier crawl'}
        laterLabel={later ? formatDateTime(later.startedAt) : 'later crawl'}
      />
    </div>
  );
}

function SiteDiffPairPage() {
  const { siteId, a, b } = siteDiffPairRoute.useParams();

  const diffQuery = useQuery({
    queryKey: ['sites', siteId, 'diff', a, b] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/diff/{a}/{b}', {
        params: { path: { siteId, a, b } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (diffQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading diff…</div>;
  }
  if (diffQuery.isError || !diffQuery.data) {
    return (
      <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
        Diff unavailable.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to="/sites/$siteId"
          params={{ siteId }}
          className="text-xs text-muted-foreground hover:underline"
          data-testid="site-diff-pair-back-link"
        >
          ← Back to site
        </Link>
        <h1 className="text-xl font-semibold">Scan diff</h1>
        <p className="text-xs text-muted-foreground">
          Earlier crawl <span className="font-mono">{a.slice(0, 8)}…</span> vs later crawl{' '}
          <span className="font-mono">{b.slice(0, 8)}…</span>
        </p>
      </div>
      <DiffReport report={diffQuery.data} earlierLabel={`crawl ${a.slice(0, 8)}`} laterLabel={`crawl ${b.slice(0, 8)}`} />
    </div>
  );
}
